import { DispatchSummaryWithMarketComments, SummaryHistoryFormDefinitions } from '../SummaryHistoryForm/types';
import { useEffect, useMemo } from 'react';
import useSession, { SessionUser } from '@/state/session';

import Popup from '@/common/Popup';
import { PopupTypes } from '@/common/Popup/types';
import SummaryHistoryForm from '../SummaryHistoryForm';
import { createComponentState } from '@/state';
import { getClasses } from '@/utils';
import useConfirmation from '@/hooks/useConfirmation';
import { useNavigate } from 'react-router-dom';

export type SummaryHistoryDrawerState = {
  show: boolean;
  data: Partial<DispatchSummaryWithMarketComments>;
};
export const initSummaryHistoryState: SummaryHistoryDrawerState = {
  show: false,
  data: {},
};
export const useSummaryHistoryDrawer = createComponentState<SummaryHistoryDrawerState>(initSummaryHistoryState);
export type SummaryHistoryDrawerProps = { locale?: Record<string, string> } & PopupTypes.Props;

const SummaryHistoryDrawer = ({ locale: customLocale, ...popupProps }: SummaryHistoryDrawerProps): JSX.Element => {
  const [state, setState] = useSummaryHistoryDrawer(({ state, setState }) => [state, setState]);
  const { show, data } = state;
  const user = useSession(({ state }) => state.user || ({} as SessionUser));
  const navigate = useNavigate();
  const confirmHide = useConfirmation({
    Body: {
      message: (
        <div className="IssueConfirmation">
          <h5>Are you sure?</h5>
          <span>Any unsaved changes will be lost.</span>
        </div>
      ),
    },
  });

  const onHide = async (redirect: boolean = false): Promise<void> => {
    try {
      if (redirect) {
        SummaryHistoryFormDefinitions.clearForm();
        setState({ ...state, show: false });
      } else {
        const issueValue = SummaryHistoryFormDefinitions.loadForm();
        const issuesHasValue = Object.keys(issueValue).length > 0;
        if (issuesHasValue) await confirmHide();
        setState({ ...state, show: false });
      }
    } catch (err) {
      console.error('Error hiding drawer:', err);
    }
  };
  const onExited = (): void => setState(initSummaryHistoryState);

  const onSubmit = (data): void => {
    debug.log('summary-history,summary-history.drawer', 'Submit Summary', data);
    SummaryHistoryFormDefinitions.clearForm();
    onHide();
  };

  const gotoHistory = (): void => {
    onHide(true);
    navigate('/summary-history');
  };

  const actions = useMemo(() => {
    const result = [];
    if (!location.pathname.startsWith('/summary-history'))
      result.push({
        key: 'history',
        label: 'See History',
        onClick: gotoHistory,
      });
    return result.length > 0 ? result : undefined;
  }, [location.pathname, gotoHistory]);

  const value = useMemo(() => {
    if (!show) return undefined;
    const isDispatcher = user?.role?.name === 'dispatch';
    const dispatcherId = isDispatcher && !data?.dispatcherId ? user?.id : data?.dispatcherId;
    const result = { ...data, dispatcherId };
    debug.log('summary-history,summary-history.drawer', 'Default Value', result);
    return result;
  }, [show, data, user]);

  useEffect((): void => {
    if (show) return;
    SummaryHistoryFormDefinitions.clearForm();
  }, [show]);

  return (
    <Popup
      {...popupProps}
      className={getClasses('SummaryHistoryDrawer', popupProps.className)}
      show={show}
      onHide={onHide}
      onExited={onExited}
      actions={actions}
    >
      <SummaryHistoryForm value={value} key={data?.id} onSubmit={onSubmit} />
    </Popup>
  );
};

export default SummaryHistoryDrawer;
